import { useState, useEffect } from 'react'
import { Menu, X, Globe } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '../lib/cn'
import { useLanguage } from '../contexts/LanguageContext'

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { language, setLanguage, t } = useLanguage()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Solo hacer scroll si estamos en la landing page
    if (location.pathname === '/' && href.startsWith('#')) {
      e.preventDefault()
      setIsMobileMenuOpen(false)
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } else if (href.startsWith('#')) {
      // Si estamos en otra página y el enlace es un hash, redirigir a la landing page
      e.preventDefault()
      setIsMobileMenuOpen(false)
      window.location.href = `/${href}`
    } else {
      setIsMobileMenuOpen(false)
    }
  }

  const toggleLanguage = () => {
    setLanguage(language === 'es' ? 'en' : 'es')
  }

  const navItems = [
    { label: t('nav.home'), href: '/', isHash: false },
    { label: t('nav.modules'), href: '#modulos', isHash: true },
    { label: t('nav.statistics'), href: '#descarga', isHash: true },
    { label: t('nav.management'), href: '#gestion', isHash: true },
    { label: t('nav.pricing'), href: '#precios', isHash: true },
    { label: t('nav.downloads'), href: '#descargas', isHash: true },
  ]

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'glass-strong border-b border-white/10 py-3'
          : 'bg-transparent py-4',
      )}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
          >
            <div className="grid size-10 place-items-center rounded-xl bg-white/10 border border-white/10 group-hover:bg-white/15 transition-colors">
              <img src="/logo.svg" alt="Nexusdevtech Logo" className="size-8" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">Nexusdevtech</div>
              <div className="text-xs text-white/60">{t('nav.subtitle')}</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              if (item.isHash) {
                // Para enlaces hash, usar <a> para permitir scroll o redirección
                return (
                  <a
                    key={item.href}
                    href={location.pathname === '/' ? item.href : `/${item.href}`}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {item.label}
                  </a>
                )
              } else {
                // Para rutas normales, usar Link
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {item.label}
                  </Link>
                )
              }
            })}
            <a
              href="https://www.nexusdevtech.com/login"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {t('nav.login')}
            </a>
            <button
              onClick={toggleLanguage}
              className="ml-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle language"
            >
              <Globe className="size-4" />
              <span className="uppercase">{language === 'es' ? 'En' : 'Es'}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden grid size-10 place-items-center rounded-lg bg-white/10 border border-white/10 hover:bg-white/15 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="size-5 text-white" />
            ) : (
              <Menu className="size-5 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => {
                if (item.isHash) {
                  // Para enlaces hash, usar <a> para permitir scroll o redirección
                  return (
                    <a
                      key={item.href}
                      href={location.pathname === '/' ? item.href : `/${item.href}`}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      {item.label}
                    </a>
                  )
                } else {
                  // Para rutas normales, usar Link
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      {item.label}
                    </Link>
                  )
                }
              })}
              <a
                href="https://www.nexusdevtech.com/login"
                className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                {t('nav.login')}
              </a>
              <button
                onClick={toggleLanguage}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                aria-label="Toggle language"
              >
                <Globe className="size-4" />
                <span>{language === 'es' ? 'English' : 'Español'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

